'use client';

import { fetchCustomerAddress } from '@/api/account';
import { refreshCarts } from '@/api/cart';
import useRouteInfo from '@/hooks/useRouteInfo';

import { createOrderForPayment, getPriceForPayment } from '@/api/order';
import { fetchPaymentMethodsList, getOrderCoupon, getPayPopData } from '@/api/pay';
import { storeUserAuthorizationLog } from '@/api/public';
import { ICreateOrderResponse, IPrice, IPriceParams, IVipEquity } from '@/api/types/order';
import { Address } from '@/api/types/public';
import { toast } from '@/components/Toast';
import useBlockNavigation from '@/hooks/useBlockNavigation';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { IS_DE_FR } from '@/utils/constants';
import { env } from '@/utils/env';
import { accSub } from '@/utils/tools';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceEffect, useUpdateEffect } from 'ahooks';
import AddressSection, { AddressActionType } from './components/Address';
import AnniversaryGift from './components/AnniversaryGift';
import BillingAddress from './components/BillingAddress';
// import BuyVip from './components/BuyVip';
import CodeCoupon from './components/CodeCoupon';
import ContinuePaymentButton from './components/ContinuePaymentButton';
import OrderSummary from './components/OrderSummary';
import PaymentMethod from './components/PaymentMethod';
import PlaceOrderButton from './components/PlaceOrderButton';
import PlaceOrderButtonWap from './components/PlaceOrderButton/index.m';
import Points from './components/Points';
import MemberShipGiftInvalid from './components/Popup/MemberShipGiftInvalid';
import RepeatPopup from './components/Popup/RepeatPopup';
import RetainPopup from './components/Popup/RetainPopup';
import ProductList from './components/ProductList';
import RightsImageWithAgreement from './components/RightsImageWithAgreement';
import ShippingAddressAndMethod from './components/ShippingAddressAndMethod';
import ShippingMethod from './components/ShippingMethod';
import WorryFreeDelivery from './components/WorryFreeDelivery';
import { usePaymentStore } from './store';
import { OrderInfo } from './types';
import { getDefaultAddress } from './util';

export enum IUseWallet {
  USE = 1,
  NOT_USE = 2,
}

// 物流保险的策略变量
export enum IWorryFreeDeliveryAbTestingData {
  NOTSHOW = 1,
  SHOW = 2,
}

interface ClientPageProps {
  initAddressList: Address[];
  isMobile: boolean;
  redeemCartId: string; // 积分兑换的购物车id
  vipEquity: IVipEquity;
  showPaymentJoinMember: any;
  paymentUuid: string;
  startTime: number;
  userAgent: string;
}

enum STEP {
  ONE = 1,
  TWO = 2,
}

export interface ILoading {
  placeOrder: boolean;
  shippingMethod: boolean;
  global: boolean;
  priceLoading: boolean;
}

export default function ClientPage({
  initAddressList,
  isMobile = true,
  redeemCartId = '',
  vipEquity,
  showPaymentJoinMember,
  paymentUuid,
  startTime,
  userAgent,
}: ClientPageProps) {
  const t = useTranslations('payment.clientPage');
  const setAddressList = usePaymentStore((state) => state.setAddressList);
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  const router = useRouter();
  const { trackEvent } = useReporting();

  const currency = useStore((state) => state.currency);
  const selectedCart = useStore((state) => state.selectedCartIds);
  const userInfo = useStore((state) => state.userInfo);
  const setUserInfo = useStore((state) => state.setUserInfo);
  const setSelectedCartIds = useStore((state) => state.setSelectedCartIds);
  const setUuidOrderList = useStore((state) => state.setUuidOrderList);
  const canBuyList = useStore((state) => state.canBuyList);
  const { getFromRouterInfo } = useRouteInfo();
  const fromRouterInfo = useRef({ refer_page_name: '', refer_url_path: '' });

  // const buyVipRef = useRef<any>(null);
  const applyPointsRef = useRef<any>(null);
  const codeCouponRef = useRef<any>(null);
  const globalPayRef = useRef<any>(null);
  const addressRef = useRef<any>(null);
  const billingAddressRef = useRef<any>(null);
  const orderSummaryRef = useRef<any>(null);
  const productListRef = useRef<any>(null);
  const rightScrollRef = useRef<any>(null);

  // 1. 用 ref 保存最新值
  const latestRef = useRef<{ cartIds: number[]; coupons: any[] }>({
    cartIds: [],
    coupons: [],
  });

  // 步骤状态
  const [step, setStep] = useState(STEP.ONE);

  // 表单状态统一管理
  // 初始化 privacyPolicy：
  // 美国站：优先使用账户信息接口返回的 order_payment_marketing_notification（1已勾选，2未勾选）
  // 其他站点默认为 true
  const initialPrivacyPolicy = useMemo(() => {
    if (env.NEXT_PUBLIC_SITE_NAME === 'us') {
      // 优先使用账户信息接口返回的值
      if (userInfo?.order_payment_marketing_notification === 1) {
        return true;
      }
      if (userInfo?.order_payment_marketing_notification === 2) {
        return false;
      }
      return false;
    }
    return true;
  }, [userInfo]);

  const [form, setForm] = useState({
    privacyPolicy: initialPrivacyPolicy,
    paymentMethods: '',
    point: 0,
    shipping_address_id: 0,
    billing_address_id: 0,
    coupon: 0,
    code: '',
    buy_vip: '0', // 是否购买 vip 0 不购买 1 购买
    freight_type: '',
    payment_type: '',
    is_same: true, // 账单地址和收货地址是否一直，true 一直，false 不一致
    wallet: IUseWallet.NOT_USE, // 是否使用钱包 1 使用钱包，2 不使用
  });

  // 如果有积分兑换购物车id，则为[id]，否则为selectedCart
  const [cartIdArray, setCartIdArray] = useState<number[]>([]);

  // 是否只显示地址
  const [onlyShowAddress, setOnlyShowAddress] = useState<boolean>(false);

  // 物流保险ab test  : 最终用户是否选择物流保险(1是 2否)
  const [isCheckDelivery, setIsCheckDelivery] = useState<number>(1);

  // 物流保险ab test  : 是否显示免费配送
  const [abShowFreeDelivery] = useState<boolean>(true);

  // 物流保险ab test  : 物流保险ab test 组
  const [abFreeDeliveryVariable] = useState<string>('2');

  const [isAllowShowRetain, setIsAllowShowRetain] = useState<boolean>(true);

  const [repeatDialogShow, setRepeatDialogShow] = useState<boolean>(false);

  const [retainDialogShow, setRetainDialogShow] = useState<boolean>(false);

  // 物流方式列表
  const [freightList, setFreightList] = useState<IPrice['freight']>([]);

  // 是否允许使用电子钱包
  const [allowWallet, setAllowWallet] = useState<boolean>(false);

  // 魔方后台配置的所有允许的支付方式
  const [paymentTypeList, setPaymentTypeList] = useState<any[]>([]);

  const [allowPayment, setAllowPayment] = useState<any[]>([]);

  const [userShippingMethod, setUserShippingMethod] = useState<string>('');

  const [price, setPrice] = useState<IPrice['price'] | null>(null);

  const [priceOrder, setPriceOrder] = useState<IPrice['order'] | null>(null);

  // const [allowBuyVip, setAllowBuyVip] = useState<boolean>(false);

  // const [switchBuyOpen, setSwitchBuyOpen] = useState<boolean>(false);
  const switchBuyOpen = false;

  const [showBillingAddress, setShowBillingAddress] = useState<boolean>(false);

  const [grandTotal, setGrandTotal] = useState<number>(0);

  const [isZero, setIsZero] = useState<boolean>(false);

  const setCannotSubmitData = useStore((state) => state.setCannotSubmitData);

  const [showMemberShipValid, setShowMemberShipValid] = useState<any>({ show: false });

  const setCannotCheckoutData = useStore((state) => state.setCannotCheckoutData);

  const [couponList, setCouponList] = useState<any[]>([]);

  const [usableCouponList, setUsableCouponList] = useState<any[]>([]);

  const [canUseLength, setCanUseLength] = useState<number>(0);

  const [discountPrice, setDiscountPrice] = useState<number>(0);

  const [payPopData, setPayPopData] = useState<any>({});

  const [deepType, setDeepType] = useState<boolean>(false);

  const [isSDPlaceOrderPageViewSend, setIsSDPlaceOrderPageViewSend] = useState<boolean>(false);

  const [isfirstChange, setIsFirstChange] = useState(true);

  const [rightScrollHeight, setRightScrollHeight] = useState<number>(0);

  // 收货地址
  const [address, setAddress] = useState<Address | null>(getDefaultAddress(initAddressList));

  const [isCreateOrderSuccess, setIsCreateOrderSuccess] = useState<boolean>(false);

  // 上一次支付的方式
  const [lastPayMethod, setLastPayMethod] = useState('');

  // 进入支付页场景
  const entrancePaymentScene = useRef<string>('');

  // 账单地址
  const [billingAddress, setBillingAddress] = useState<Address | null>(
    getDefaultAddress(initAddressList),
  );

  const [loading, setLoading] = useState<ILoading>({
    placeOrder: false,
    shippingMethod: false,
    global: false,
    priceLoading: false,
  });

  const [shippingMethodValid, setShippingMethodValid] = useState({
    shippingMethodError: true,
    shippingMethodErrorMessage: '',
    paymentMethodError: true,
    paymentMethodErrorMessage: '',
    billingAddressError: true,
    billingAddressErrorMessage: '',
  });

  const [gtagItems, setGtagItems] = useState<any[]>([]);

  // 用来管理路由的跳转
  const [nextRoute, setNextRoute] = useState<string | null>();

  // 在非积分兑换的支付页中上报GA4埋点，只在首次加载的时候上报
  const hasReportBeginCheckoutGA = useRef<boolean>(false);

  useEffect(() => {
    // setIsGlobalLoading(true);
    setAddressList(initAddressList);

    // setAddress(getDefaultAddress(initAddressList));
    // setBillingAddress(getDefaultAddress(initAddressList));
    // setIsGlobalLoading(false);
  }, []);

  // 使用useMemo实现计算属性
  const walletPayAmount = useMemo(() => {
    if (form.wallet === IUseWallet.USE) {
      return form.freight_type
        ? freightList.find((item) => item.name === form.freight_type)?.expend_wallet || '0.00'
        : price?.expend_wallet;
    }
    return '0.00';
  }, [form.wallet, form.freight_type, freightList, price]);

  const payAmountWithWallet = useMemo(() => {
    if (form.wallet === IUseWallet.USE) {
      const walletBalance = parseFloat(walletPayAmount);
      const _grandTotal = parseFloat(grandTotal.toString());
      return walletBalance >= _grandTotal
        ? '0.00'
        : parseFloat(accSub(_grandTotal, walletBalance)).toFixed(2);
    }
    return '0.00';
  }, [form.wallet, walletPayAmount, grandTotal]);

  const quoteId = useMemo(() => {
    return freightList.find((item) => item.name === form.freight_type)?.insurance?.quote_id || '';
  }, [freightList, form.freight_type]);

  const allUsingWallet = useMemo(() => {
    return (
      form.wallet === IUseWallet.USE &&
      form.buy_vip === '0' &&
      parseFloat(price?.wallet_balance) >= parseFloat(grandTotal.toString())
    );
  }, [form.wallet, form.buy_vip, price?.wallet_balance, grandTotal]);

  const shippingMethod: any = useMemo(() => {
    return freightList.find((item) => item.name === form.freight_type) || {};
  }, [freightList, form.freight_type]);

  const isVip = useMemo(() => {
    const rule1 = ['US', 'CA', 'GB'].includes(address?.country_id); // 只有美国、加拿大能购买会员
    const rule2 = userInfo.type === 2 || form.buy_vip === '1'; // 要么是vip，要么本次购买vip
    return rule1 && rule2;
  }, [address, userInfo, form.buy_vip]);

  const showWallet = useMemo(() => {
    const rule2 = parseFloat(price?.wallet_balance || '0') > 0; // 用户电子钱包余额 大于 0
    const rule3 = [1, 13].includes(Number(priceOrder?.order_type)); // 订单类型为普通订单和测试订单
    const rule4 = allowWallet; // 魔方电子钱包支付状态为开启
    const rule5 = !redeemCartId; // 非积分商城订单

    return rule2 && rule3 && rule4 && rule5;
  }, [price, priceOrder, allowWallet, redeemCartId]);

  const placeOrderDisable = useMemo(() => {
    if (!redeemCartId) {
      // 非积分兑换订单
      return step === STEP.ONE;
    } else if (isZero) {
      // 积分兑换且0元
      return false;
    } else {
      // 积分兑换非0元
      return step === STEP.ONE;
    }
  }, [redeemCartId, isZero, step]);

  const shippingActuallyPrice = useMemo(() => {
    if (form.freight_type && form.shipping_address_id) {
      return freightList.find((item) => item.name === form.freight_type)?.price;
    }
    return '0';
  }, [form.freight_type, form.shipping_address_id, freightList]);

  // 物流保险金额（埋点使用）
  const cargoInsuranceAmount = useMemo(() => {
    return (
      +freightList.find((item) => item.name === form.freight_type)?.insurance?.insurance_price || 0
    );
  }, [freightList, form.freight_type]);

  // 获取可用的支付方式
  const getAllowPayment = async () => {
    const { data } = await fetchPaymentMethodsList();
    // 查询是否允许使用电子钱包
    setAllowWallet(data.payment_methods.some((item) => item.payment_method === 'wallet'));
    // 魔方后台配置的所以允许的支付方式
    setPaymentTypeList(data.payment_methods);
    // pc 和 移动有不同的过滤范围
    const filterList = isMobile ? ['wallet'] : ['googlepay', 'applepay', 'wallet'];
    const ap = data.payment_methods
      .filter((item) => !filterList.includes(item.payment_method))
      .map((item) => item.payment_method);
    setAllowPayment(ap);
  };

  //* **计价方法 code start

  const handleGetPriceParams = () => {
    const params: IPriceParams = {
      cart_ids: cartIdArray, // 购物车id
      currency: currency.name, // 货币名称
      point: form.point, // 积分
      address_id: address?.id, // 地址id
      coupon: form.coupon, // 优惠券
      code: form.code, // 优惠码
      buy_vip: form.buy_vip, // 购买vip
      freight_type: form.freight_type, // 物流方式
      payment_type: form.payment_type, // 支付方式
      wallet: form.wallet.toString(), // 钱包
      is_insurance: isCheckDelivery.toString(), // 是否选择物流保险
      is_show_seel: abShowFreeDelivery ? '1' : '2', // 是否显示免费配送
      seel_ab_test_group: abFreeDeliveryVariable,
    };

    if (redeemCartId) {
      // 积分商城订单
      params.order_type = 14;
    }

    return params;
  };

  // react 版本的改造，支持参数的传递。
  const getPrice = async (additionalParams = {}) => {
    const params = {
      ...handleGetPriceParams(),
      ...additionalParams,
    };

    setLoading((prev) => {
      return {
        ...prev,
        priceLoading: true,
      };
    });

    const response = await getPriceForPayment(params);
    const { code, msg, data } = response;

    setIsAllowShowRetain(![400100, 400300].includes(code));

    switch (code) {
      case 400300:
        // 订单在其他端已创建
        handleCode400300();
        break;
      case 400100:
        // 商品未通过校验
        handleCode400100(data);
        break;
      case 400200:
        // 优惠券/优惠码无效
        handleCode400200(msg);
        break;
      case 400400:
        // 地址在其他端删除
        handleCode400400(msg);
        break;

      case 400700:
        // 支付方式不可用
        handleCode400700(msg);
        break;
      case 200:
        handleCode200(data as IPrice & { last_payment_method: string });
        break;
      default:
        break;
    }

    setLoading((prev) => {
      return {
        ...prev,
        priceLoading: false,
      };
    });

    return response; // 返回API响应结果
  };

  const onAddressChange = async (ads: Address) => {
    const isAddressIdChanged = address?.id !== ads.id;
    setAddress(ads);

    if (form.is_same) {
      setBillingAddress(ads);

      setForm((prev) => {
        return {
          ...prev,
          billing_address_id: ads.id,
          buy_vip: '0',
        };
      });

      // 取消校验
      setShippingMethodValid({
        ...shippingMethodValid,
        billingAddressError: true,
        billingAddressErrorMessage: '',
      });
    }

    setForm((prev) => {
      return {
        ...prev,
        shipping_address_id: ads.id,
        // billing_address_id: ads.id,
        // 只有在切换到不同地址时才清空物流方式，编辑当前地址时保留原物流方式
        freight_type: isAddressIdChanged ? '' : prev.freight_type,
      };
    });
    trackEvent(
      {
        eventName: 'address_click',
        properties: {
          event_label: `ShippingAddress::${address.firstname} ${address.lastname} / ${address.address} / ${address.telephone}`,
          event_category: 'CHECKOUT',
        },
      },
      [ProviderType.GA4],
    );
  };

  const onBillingAddressChange = (_address: Address) => {
    if (!_address) return;
    setBillingAddress(_address);
    setForm((prev) => {
      return {
        ...prev,
        billing_address_id: _address.id,
      };
    });
  };

  const verifyBillingAddress = () => {
    if (billingAddressRef.current && !billingAddressRef.current.countryIsSame) {
      setShippingMethodValid((prev) => ({
        ...prev,
        billingAddressError: false,
        billingAddressErrorMessage:
          'The country of Billing address should be consistent with the country of Shipping address',
      }));
    } else {
      setShippingMethodValid((prev) => ({
        ...prev,
        billingAddressError: true,
        billingAddressErrorMessage: '',
      }));
    }
  };

  useEffect(() => {
    verifyBillingAddress();
  }, [billingAddress, form.payment_type, form.is_same]);

  const onChangeBillFlag = (data: { flag: boolean; address?: Address }) => {
    setForm((prev) => {
      return {
        ...prev,
        is_same: data.flag,
      };
    });
    if (data.flag) {
      setBillingAddress(address);
    }
  };

  const { isAttemptingNavigation, proceedNavigation, cancelNavigation } = useBlockNavigation(
    isAllowShowRetain,
    ['/', '/paymentSuccess', '/paymentFailed'],
  );

  // 处理页面初始化时的业务逻辑
  const handlePageView = () => {
    if (
      localStorage.getItem('zl-selectedCart') &&
      (JSON.parse(localStorage.getItem('zl-selectedCart')) as number[]).length
    ) {
      setSelectedCartIds(JSON.parse(localStorage.getItem('zl-selectedCart')) as number[]);
    }

    sessionStorage.removeItem('zl_payment_join_free_uuid');
    if (
      JSON.parse(localStorage.getItem('zl-uuidOrderList')) &&
      (JSON.parse(localStorage.getItem('zl-uuidOrderList')) as number[]).length
    ) {
      setUuidOrderList(JSON.parse(localStorage.getItem('zl-uuidOrderList')) as number[]);
    }

    SDPlaceOrderPageView();

    setRightScrollHeight(computeRightScrollHeight());
  };
  const computeRightScrollHeight = () => {
    const screenHeight = window.innerHeight;
    const btnHeight = document.getElementById('right-button-offset-height')?.offsetHeight; // placeorder按钮模块高度
    const _rightScrollHeight = screenHeight - btnHeight - 60;
    return _rightScrollHeight;
  };

  // 根据后端接口返回决定是否需要弹留存弹窗
  const handleRouterLeave = async (cartIds: number[], coupons: any[]): Promise<boolean> => {
    // 1. 前置条件判定：无需弹窗，直接离开
    if (!isAllowShowRetain) {
      SDPlaceOrderPageLeave();
      return true;
    }
    if (cartIds.length === 0) {
      SDPlaceOrderPageLeave();
      return true;
    }

    const normalType = Boolean(coupons.length);
    setDeepType(!!normalType);

    const { data } = await getPayPopData({
      is_have_coupon: coupons.length > 0 ? 1 : 2,
      cart_ids: cartIds,
      currency: currency.name,
    });

    setPayPopData(data.data);

    // 2. status===1 代表后端要求弹窗阻拦，否则直接放行
    if (data.data.status === 1) {
      setRetainDialogShow(true);
      return false; // 阻拦
    }

    return true; // 允许离开
  };

  const init = async () => {
    handlePageView();
    // getWorryFreeDeliveryAbTestingData();
    await getAllowPayment();
  };

  // 每次渲染都同步最新状态到 ref（无依赖）
  useEffect(() => {
    latestRef.current = { cartIds: cartIdArray, coupons: couponList };
  });
  useEffect(() => {
    fromRouterInfo.current = getFromRouterInfo();
    entrancePaymentScene.current =
      sessionStorage.getItem('zl-sensors-entrance-payment-scene') || '其他';
    sessionStorage.removeItem('zl-sensors-entrance-payment-scene');

    // 设置页面背景色
    document.body.style.backgroundColor = '#f5f6f7';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);
  // 当检测到用户尝试离开页面时，再进行一次后端确认
  useEffect(() => {
    if (isAttemptingNavigation) {
      const { cartIds, coupons } = latestRef.current;

      (async () => {
        const canLeave = await handleRouterLeave(cartIds, coupons);
        logger.log('可以离开吗？', canLeave);
        if (canLeave) {
          SDPlaceOrderPageLeave();
          proceedNavigation(nextRoute);
        } else {
          cancelNavigation();
        }
      })();
    }
  }, [isAttemptingNavigation]);

  useEffect(() => {
    if (redeemCartId) {
      setNextRoute('/account/points-mall');
      setCartIdArray([Number(redeemCartId)]);
      SDPlaceOrderPageLeave();
    } else {
      setNextRoute('/cart?from=payment');
      SDPlaceOrderPageLeave();

      if (
        selectedCart.length === 0 &&
        localStorage.getItem('zl-selectedCart') &&
        (JSON.parse(localStorage.getItem('zl-selectedCart')) as number[]).length
      ) {
        setCartIdArray(JSON.parse(localStorage.getItem('zl-selectedCart')) as number[]);

        return;
      }

      setCartIdArray(selectedCart);
    }
  }, []);

  useEffect(() => {
    if (cartIdArray.length > 0) {
      init();
    }
    if (!redeemCartId && cartIdArray.length > 0) {
      getCouponList();
    }
  }, [cartIdArray]);

  useEffect(() => {
    if (
      !redeemCartId &&
      cartIdArray.length > 0 &&
      price &&
      grandTotal &&
      !hasReportBeginCheckoutGA.current
    ) {
      hasReportBeginCheckoutGA.current = true;
      // 因为GA4上报的埋点依赖于price，所以需要加这个判断price不为空
      sendBeginCheckoutGA();
    }
  }, [cartIdArray, price, grandTotal]);

  const getPriceAfterDependence = async () => {
    setIsGlobalLoading(true);
    await getPrice();
    setIsGlobalLoading(false);
  };

  // 防抖触发状态
  const [shouldTriggerPriceUpdate, setShouldTriggerPriceUpdate] = useState(false);

  // 请求价格核心逻辑，依赖必须严格控制。
  useUpdateEffect(() => {
    if (allowPayment.length > 0) {
      setShouldTriggerPriceUpdate(true);
    }
  }, [form.buy_vip, form.point, form.wallet, allowPayment, address]);

  // 使用防抖来执行价格更新
  useDebounceEffect(
    () => {
      if (shouldTriggerPriceUpdate) {
        getPriceAfterDependence();
        setShouldTriggerPriceUpdate(false);
      }
    },
    [shouldTriggerPriceUpdate],
    { wait: 200 },
  );

  useEffect(() => {
    if (freightList.length > 0 && address) {
      checkAllowBuyVip();
    }
  }, [freightList, address]);

  useEffect(() => {
    if (form.payment_type === 'paypal') {
      setShowBillingAddress(false);
    } else {
      setShowBillingAddress(true);
    }
  }, [form.payment_type]);

  // 状态码处理
  const handleCode400300 = () => {
    setRepeatDialogShow(true);
  };

  const handleCode400100 = (data: IPrice) => {
    setCannotSubmitData({
      ...data,
      show: true,
      confirmBtnText: !redeemCartId ? 'Back to Cart' : 'OK',
      onClose: () => {
        goCart();
      },
    });
    setLoading((prev) => {
      return {
        ...prev,
        global: false,
      };
    });
  };

  const handleCode400200 = (msg: string) => {
    toast.error(msg);
  };

  const handleCode400400 = (msg: string) => {
    setLoading((prev) => {
      return {
        ...prev,
        placeOrder: false,
      };
    });
    toast.error(msg);
    setFreightList([]);
  };

  const handleCode400700 = (msg: string) => {
    setLoading((prev) => {
      return {
        ...prev,
        placeOrder: false,
        global: false,
      };
    });
    toast.error(msg);
    getAllowPayment();
  };

  const handleCode200 = (data: IPrice & { last_payment_method: string }) => {
    const { freight } = data;
    if (userShippingMethod) {
      freight.forEach((_item) => {
        if (_item.name === userShippingMethod) {
          _item.default = 1;
        } else {
          _item.default = 0;
        }
      });
    }
    setFreightList(address ? freight : []);

    setPrice(data.price);
    setPriceOrder(data.order);
    rightScrollFunc();

    const last_payment_method = data.last_payment_method || '';
    let lastPaymentType = '';
    const foundItem = paymentTypeList.find((item) => item.payment_method === last_payment_method);
    if (foundItem) {
      lastPaymentType = foundItem.payment_method;
    }

    if (
      allowPayment.includes(lastPaymentType) &&
      form.payment_type === '' &&
      form.wallet === IUseWallet.NOT_USE &&
      globalPayRef.current
    ) {
      // 注意：会在 changePayment 校验 lastPaymentType 是否可用
      setLastPayMethod(lastPaymentType);
    }
  };

  const goToPlaceOrder = async () => {
    SDPlaceOrderPagePlaceOrderBtnClick();

    if (!redeemCartId) {
      sendGAplaceOrderClick();
    }

    setLoading((prev) => {
      return {
        ...prev,
        placeOrder: true,
      };
    });

    const valid = await verifyOrder();

    if (!valid) {
      setLoading((prev) => {
        return {
          ...prev,
          placeOrder: false,
        };
      });
      return;
    }

    try {
      if (isZero) {
        // 积分兑换0元
        const order = await createOrder();
        if (order) {
          sessionStorage.setItem('zl-payFlag', '1');
          setIsGlobalLoading(true);
          window.location.replace(
            `/paymentSuccess?order_id=${order.order_id}&order_no=${order.order_no}`,
          );
        }
      } else if (!['googlepay', 'applepay'].includes(form.payment_type)) {
        const order = await createOrder();
        if (order) {
          globalPayRef.current.payAction(order);
        }
      } else if (['googlepay', 'applepay'].includes(form.payment_type)) {
        // google 和苹果支付
        logger.log('打印-1 globalPayRef.current', globalPayRef.current);
        globalPayRef.current.payAction();
      }
    } catch (error) {
      logger.log('error', error);
      setLoading((prev) => {
        return {
          ...prev,
          placeOrder: false,
        };
      });
    }
  };

  const scroll = (_scroll: number) => {
    logger.log('_scroll', _scroll);
    // 滑动到指定位置
    isMobile &&
      setTimeout(() => {
        const errorSection = document.querySelector('.error-section');
        if (errorSection) {
          errorSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 10);
  };

  const verifyOrder = useCallback(async () => {
    // wap 和 pc 校验的交互方式不同，需要不同的处理。

    const flag = false;

    if (isMobile) {
      // wap 校验

      let addressScroll = 0;
      let shippingScroll = 0;
      let paymentScroll = 0;
      let billingScroll = 0;
      const errorSet = new Set();
      const errorMsg = new Set();
      if (!form.shipping_address_id) {
        addressRef.current.checkValid();
        addressScroll = 1;
        errorSet.add('00407');
        errorMsg.add('no_save_shipping');
      }

      // 判断校验状态
      if (!address?.checked) {
        addressRef.current.checkValid();
        addressScroll = 1;
        errorSet.add('00401');
        errorMsg.add('no_shipping');
      }

      // 校验物流方式是否为空
      if (!form.freight_type) {
        shippingScroll = 1;
        errorSet.add('00403');
        errorMsg.add('no_shipping_method');
        setShippingMethodValid((prev) => ({
          ...prev,
          shippingMethodError: false,
          shippingMethodErrorMessage: 'Please Select Shipping Method',
        }));
      }

      const paymentRule1 =
        form.wallet === 1 &&
        (Number(price.wallet_balance) - grandTotal < 0 || parseFloat(payAmountWithWallet) > 0) &&
        !form.payment_type;
      const paymentRule2 = form.wallet === 2 && !form.payment_type && !isZero;

      if (paymentRule1 || paymentRule2) {
        paymentScroll = 1;
        toast.error(t('pleaseSelectPaymentMethod'));
        errorSet.add('00404');
        errorMsg.add('no_payment_method');

        setShippingMethodValid((prev) => ({
          ...prev,
          paymentMethodError: false,
          paymentMethodErrorMessage: 'Please Select Payment Method',
        }));
      }

      // Zing 会员不可使用电子钱包支付
      if (form.wallet === 1 && form.buy_vip === '1' && !form.payment_type) {
        paymentScroll = 1;
        errorSet.add('00404');
        errorMsg.add('no_payment_method');

        setShippingMethodValid((prev) => ({
          ...prev,
          paymentMethodError: false,
          paymentMethodErrorMessage:
            'Store Credit not available for Zing Members. Select another payment method',
        }));
      }

      if (!form.is_same) {
        if (!billingAddress?.country_id) {
          billingScroll = 1;
          errorSet.add('00405');
          errorMsg.add('no_billing');

          setShippingMethodValid((prev) => ({
            ...prev,
            billingAddressError: false,
            billingAddressErrorMessage: 'Please add your billing address',
          }));
        }
      }

      if (!billingAddress?.checked) {
        billingScroll = 1;
        errorSet.add('00405');
        errorMsg.add('no_billing');

        setShippingMethodValid((prev) => ({
          ...prev,
          billingAddressError: false,
          billingAddressErrorMessage: 'Please add your billing address ',
        }));
      }

      if (billingAddressRef.current && !billingAddressRef.current.countryIsSame) {
        billingScroll = 1;
        errorSet.add('00405');
        errorMsg.add('no_billing');

        setShippingMethodValid((prev) => ({
          ...prev,
          billingAddressError: false,
          billingAddressErrorMessage:
            'The country of Billing address should be consistent with the country of Shipping address',
        }));
      }

      if (addressScroll > 0) {
        scroll(addressScroll);
        if (errorSet.size > 0) {
          SDPlaceOrderPagePlaceOrderResult(
            '',
            false,
            Array.from(errorSet).join(','),
            Array.from(errorMsg).join('@'),
          );
        }
        return false;
      }

      if (shippingScroll > 0) {
        scroll(shippingScroll);
        if (errorSet.size > 0) {
          SDPlaceOrderPagePlaceOrderResult(
            '',
            false,
            Array.from(errorSet).join(','),
            Array.from(errorMsg).join('@'),
          );
        }
        return false;
      }

      if (paymentScroll > 0 && !isZero) {
        scroll(paymentScroll);
        if (errorSet.size > 0) {
          SDPlaceOrderPagePlaceOrderResult(
            '',
            false,
            Array.from(errorSet).join(','),
            Array.from(errorMsg).join('@'),
          );
        }
        return false;
      }

      if (billingScroll > 0) {
        scroll(billingScroll);
        if (errorSet.size > 0) {
          SDPlaceOrderPagePlaceOrderResult(
            '',
            false,
            Array.from(errorSet).join(','),
            Array.from(errorMsg).join('@'),
          );
        }
        return false;
      }

      const res = await fetchCustomerAddress();
      const _addressList = res.data.list;
      const findAddress = _addressList.find((item) => item.id === address.id);

      if (!findAddress) {
        toast.error(t('addressHasBeenDeleted'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00411');
        shippingScroll = 1;
        addressRef.current.checkValid();
        return;
      }
      if (
        !findAddress ||
        (findAddress && findAddress.updated_at !== address.updated_at) ||
        (findAddress && findAddress.status === 2)
      ) {
        toast.error(t('pleaseChangeYourAddress'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00412', 'address_invalid');
        shippingScroll = 1;
        addressRef.current.checkValid();
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return false;
      }

      return true;
    } else {
      // pc 校验

      // 校验地址是否是新增编辑状态
      if (addressRef.current.actionType !== AddressActionType.DEFAULT) {
        toast.error(t('pleaseSaveAddress'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00407');
        return flag;
      }

      if (!address.checked) {
        toast.error(t('pleaseEnterSelectYourShippingAddress'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00401');
        return flag;
      }

      // 校验物流方式是否为空
      logger.log('form.freight_type', form.freight_type);
      if (!form.freight_type) {
        toast.error(t('pleaseSelectShippingMethod'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00403');
        return flag;
      }

      if (step === STEP.ONE && !isZero) {
        toast.error(t('pleaseSelectShippingMethod'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00408');
        return flag;
      }

      // Zing 会员不可使用电子钱包支付
      if (form.wallet === 1 && form.buy_vip === '1' && !form.payment_type) {
        toast.error(t('storeCreditNotAvailableForZingMembers'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00404');
        return flag;
      }

      // 校验付款方式是否为空
      // 必选支付方式条件：使用钱包且钱包余额不满足全额支付，或者 不使用钱包订单金额不为 0
      // rule1: 使用钱包 && (钱包余额小于订单金额 || 全额支付中包含有钱包不可支付部分)，但是没有选择支付方式，需要 toast 提示
      // rule2: 没有使用钱包 && 非 0 元支付订单 但是没有选择支付方式，需要 toast 提示
      const paymentRule1 =
        form.wallet === 1 &&
        (Number(price.wallet_balance) - grandTotal < 0 || parseFloat(payAmountWithWallet) > 0) &&
        !form.payment_type;
      const paymentRule2 = form.wallet === 2 && !form.payment_type && !isZero;

      if (paymentRule1 || paymentRule2) {
        toast.error(t('pleaseSelectPaymentMethod'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00404');
        return flag;
      }

      // 校验shipping地址是否是新增编辑状态
      if (
        billingAddressRef.current &&
        billingAddressRef.current.getActionType() !== AddressActionType.DEFAULT
      ) {
        toast.error(t('pleaseSaveAddress'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00409');
        return flag;
      }

      if (!form.is_same) {
        if (!billingAddress?.country_id) {
          toast.error(t('pleaseChangeYourAddress'));
          SDPlaceOrderPagePlaceOrderResult('', false, '00405');
          return flag;
        }
      }

      if (!billingAddress?.checked) {
        toast.error(t('pleaseEnterSelectYourBillingAddress'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00405');
        return flag;
      }

      // 校验账单地址国家与收货地址国家是否一致
      if (billingAddressRef.current && !billingAddressRef.current.countryIsSame) {
        toast.error(
          'The country of Billing address should be consistent with the country of Shipping address',
        );
        SDPlaceOrderPagePlaceOrderResult('', false, '00405');
        return flag;
      }

      // 美国站不需要校验 privacyPolicy（短信营销协议是可选的），其他站点需要校验
      if (env.NEXT_PUBLIC_SITE_NAME !== 'us' && !form.privacyPolicy) {
        logger.log('form.privacyPolicy', form.privacyPolicy);
        toast.error(t('pleaseAgreeToOurPrivacyPolicyBeforePlacingYourOrder'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00410');
        return flag;
      }

      const res = await fetchCustomerAddress();
      const _addressList = res.data.list;
      const findAddress = _addressList.find((item) => item.id === address.id);
      if (!findAddress) {
        toast.error(t('addressHasBeenDeleted'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00411');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }

      if (
        !findAddress ||
        (findAddress && findAddress.updated_at !== address.updated_at) ||
        (findAddress && findAddress.status === 2)
      ) {
        toast.error(t('pleaseChangeYourAddress'));
        SDPlaceOrderPagePlaceOrderResult('', false, '00412');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return false;
      }

      return true;
    }
  }, [
    t,
    form,
    address,
    addressRef,
    billingAddress,
    billingAddressRef,
    isMobile,
    isZero,
    price,
    grandTotal,
    payAmountWithWallet,
    paymentTypeList,
    shippingMethod,
    step,
  ]);

  const createOrderRef = useRef<() => Promise<any>>(() => Promise.resolve(null));

  const formRef = useRef(form);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  // 确保每次调用都能够重新创建函数
  const createOrder = useCallback(async () => {
    setIsGlobalLoading(true);

    const _form = formRef.current;

    const valid = await verifyOrder();

    if (!valid) {
      setIsGlobalLoading(false);
      return;
    }

    const _paymentType = _form.payment_type || 'wallet';

    try {
      // 自定义 Facebook 事件（跟业务确认过是在用的）
      trackEvent(
        {
          eventName: _paymentType,
          properties: {},
        },
        [ProviderType.FACEBOOK],
      );
    } catch (e) {
      logger.log(e);
    }

    const paymentTypes = paymentTypeList.find((item) => item.payment_method === _paymentType); // 取支付路由版本中的 types 数组第一个字段

    const params: any = {
      buy_vip: _form.buy_vip,
      cart_ids: cartIdArray,
      code: _form.code,
      coupon: _form.coupon,
      currency: currency.name,
      freight_type: _form.freight_type,
      payment_type: paymentTypes.payment_type, // 取支付路由版本的支付方式列表中 types 数组第一个值创建订单
      payment_channel: paymentTypes.payment_channel,
      payment_method: paymentTypes.payment_method,
      point: _form.point,
      shipping_address_id: _form.shipping_address_id,
      billing_address_id: _form.is_same ? _form.shipping_address_id : _form.billing_address_id,
      wallet: _form.wallet.toString(),
      have_member_gift: 0, // 1:包含入会下单送的赠品，0：不包含，默认0
      is_insurance: isCheckDelivery, // 1:是  2:否
      quote_id: quoteId,
    };

    if (redeemCartId) {
      // 积分商城订单
      params.order_type = 14;
    }
    if (isZero) {
      // 0元商品
      params.payment_type = 'skip';
    }

    // 当使用钱包时，需要把使用钱包支付金额传递给后端
    if (_form.wallet === IUseWallet.USE) {
      params.expend_wallet =
        freightList.find((item) => item.name === params.freight_type).expend_wallet || 0;

      // 如果全额使用钱包支付,则支付方式使用 wallet
      if (allUsingWallet) {
        params.payment_type = 'wallet';
      }
    }

    if (shippingMethod.freight_discount_ticket_id) {
      // 如果当前物流方式应用了运费券需要把券码传递后端
      params.freight_discount_ticket_id = shippingMethod.freight_discount_ticket_id;
      params.freight_price = shippingMethod.price;
    }

    // paypal 支付时强制 billing 地址 和 shipping 地址保持一致
    if (_form.payment_type === 'paypal') {
      params.billing_address_id = params.shipping_address_id;
    }

    setLoading((prev) => {
      return {
        ...prev,
        placeOrder: true,
      };
    });

    // 美国站：如果用户勾选了短信营销协议，执行用户授权记录接口
    if (env.NEXT_PUBLIC_SITE_NAME === 'us' && _form.privacyPolicy) {
      try {
        await storeUserAuthorizationLog({ content_id: 1, status: 1 });
        // 接口调用成功后，手动更新用户信息中的 order_payment_marketing_notification 字段为 1
        if (userInfo) {
          setUserInfo({
            ...userInfo,
            order_payment_marketing_notification: 1,
          });
        }
      } catch (error) {
        logger.error('Failed to store user authorization log:', error);
      }
    }

    const { data, msg, code } = await createOrderForPayment(params);
    setIsAllowShowRetain(![400100, 400300, 400880].includes(code));

    if (data.code > 0) {
      // @ts-ignore
      SDPlaceOrderPagePlaceOrderResult('', false, data.code, data.content);
    }

    if (code !== 200) {
      SDPlaceOrderPagePlaceOrderResult('', false, code, msg);
    }

    setIsGlobalLoading(false);
    switch (code) {
      case 400100:
        handleCode400100ForCreateOrder(data);
        break;
      case 400300:
        handleCode400300ForCreateOrder();
        break;
      case 400400:
        handleCode400400ForCreateOrder(msg);
        break;
      case 400700:
        handleCode400700ForCreateOrder(msg);
        break;
      case 400880:
        handleCode400880ForCreateOrder(data);
        break;
      case 400900:
        handleCode400900ForCreateOrder(msg);
        break;
      case 610:
        handleCode610ForCreateOrder(msg);
        break;
      case 200:
        return handleCode200ForCreateOrder(data);
      case 409:
        handleCode409ForCreateOrder();
        break;
      default:
        toast.error(msg);
        break;
    }

    setLoading((prev) => {
      return {
        ...prev,
        placeOrder: false,
      };
    });
  }, [
    verifyOrder,
    form,
    address,
    addressRef,
    billingAddress,
    billingAddressRef,
    isMobile,
    isZero,
    price,
    grandTotal,
    payAmountWithWallet,
    paymentTypeList,
    shippingMethod,
    step,
    fetchCustomerAddress,
    toast,
    cartIdArray,
    currency,
    freightList,
    allUsingWallet,
    isCheckDelivery,
  ]);

  // 每次 render 后把最新实现同步到 ref
  useEffect(() => {
    createOrderRef.current = createOrder;
  }, [createOrder]);

  // ---------- 对外暴露一个引用固定的调用器 ----------
  const stableCreateOrder = useCallback(async () => {
    return await createOrderRef.current();
  }, []);

  const handleCode409ForCreateOrder = () => {
    return {
      order_id: '10039425',
    };
  };

  // 商品未通过校验
  const handleCode400100ForCreateOrder = (data: ICreateOrderResponse) => {
    // 商品未通过校验
    if (data.code === 8 || data.code === 9) {
      // 8会员入会送的赠品库存不足更换商品，9会员入会送的赠品库存不足,备用品也库存不足
      // 单独处理会员入会的弹窗
      setShowMemberShipValid({
        ...data,
        show: true,
      });
    } else {
      setCannotSubmitData({
        ...data,
        show: true,
        confirmBtnText: !redeemCartId ? 'Back to Cart' : 'OK',
        onClose: () => {
          goCart();
        },
      });
    }
    setLoading((prev) => {
      return {
        ...prev,
        global: false,
      };
    });
  };

  // 订单在其他端已创建
  const handleCode400300ForCreateOrder = () => {
    setRepeatDialogShow(true);
    setLoading((prev) => {
      return {
        ...prev,
        global: false,
      };
    });
  };

  // 地址在其他端删除
  const handleCode400400ForCreateOrder = (msg: string) => {
    setLoading((prev) => {
      return {
        ...prev,
        placeOrder: false,
      };
    });
    toast.error(msg);
  };

  // 支付方式不可用
  const handleCode400700ForCreateOrder = (msg: string) => {
    setLoading((prev) => {
      return {
        ...prev,
        placeOrder: false,
        global: false,
      };
    });
    toast.error(msg);
    getAllowPayment();
  };

  // 套装活动过期
  const handleCode400880ForCreateOrder = (data: any) => {
    setLoading((prev) => {
      return {
        ...prev,
        placeOrder: false,
        global: false,
      };
    });

    setCannotCheckoutData({
      title: data.title,
      list: [
        {
          content: data.content,
          products: data.products,
        },
      ],
      show: true,
      onClose: () => {
        goCart();
      },
    });

    refreshCarts();
  };

  // 运费券过期，需要重新刷新计价接口
  const handleCode400900ForCreateOrder = (msg: string) => {
    setLoading((prev) => {
      return {
        ...prev,
        placeOrder: false,
        global: false,
      };
    });
    toast.error(msg);
    getPrice();
  };

  const handleCode610ForCreateOrder = (msg: string) => {
    toast.error(msg);
    setTimeout(() => {
      location.reload();
    }, 3000);
  };

  const handleCode200ForCreateOrder = (data: OrderInfo) => {
    SDPlaceOrderPagePlaceOrderResult(data.order_no.toString(), true, '200');
    // 全额使用钱包支付，则需要跳转支付成功页
    if (allUsingWallet) {
      sessionStorage.setItem('zl-payFlag', '1');
      setIsGlobalLoading(true);
      location.href = `/paymentSuccess?order_id=${data.order_id}&order_no=${data.order_no}`;
    }
    const _data = { ...data, shipping_address_info: address, billing_address_info: address };

    if (!isZero) {
      // 计算商品总数量
      const totalQuantity = gtagItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

      trackEvent(
        undefined,
        [ProviderType.GA4, ProviderType.FACEBOOK, ProviderType.TWITTER, ProviderType.TIKTOK],
        {
          grandNum: grandTotal, // 注意：此字段实际是订单总金额，不是数量
          totalQuantity, // 商品总数量
          orderRes: _data,
          gtagItems,
          currencyName: currency.name,
          grandTotal,
          discount: price.discount ? price.discount.replace(/[^\d.]/g, '') : 0,
        },
        'createorder.success',
      );
    }

    return _data;
  };

  const changeShippingMethod = (_shippingMethod: string, _type: boolean) => {
    if (!_shippingMethod) return;

    // TODO: 老站M端实测，切换地址是不保留上次的物流地址的
    // isMobile && setUserShippingMethod(_shippingMethod);

    // 取消校验的报错
    setShippingMethodValid({
      ...shippingMethodValid,
      shippingMethodError: true,
      shippingMethodErrorMessage: '',
    });

    setForm((prev) => {
      return {
        ...prev,
        freight_type: _shippingMethod,
      };
    });
    setIsFirstChange(false);

    if (_type || isfirstChange) {
      trackEvent(
        {
          eventName: 'add_shipping_method',
          properties: {
            event_label: _shippingMethod,
            event_category: 'CHECKOUT',
          },
        },
        [ProviderType.GA4],
      );
    }
  };

  const checkAllowBuyVip = async () => {
    if (env.NEXT_PUBLIC_ZING_MODULE_ENABLE !== 'true') {
      return;
    }
    if (vipEquity.optional_country && address && address.country_id) {
      const allowCountry = vipEquity.optional_country.find(
        (item) => item.country_id === address?.country_id,
      );
      const _isVip = userInfo.type === 2;
      const condition1 = freightList.some(
        (item) => item.name === 'business' && item.forbidden === 0,
      );
      const condition2 = !redeemCartId; // 检查是否为空字符串或null或undefined
      const condition3 = !_isVip;
      const condition4 = !!allowCountry;
      const canBuyVip = condition1 && condition2 && condition3 && condition4;

      // setAllowBuyVip(canBuyVip);
      // 当不允许购买vip，但是vip之前勾选过，那么就取消勾选
      if (!canBuyVip && form.buy_vip === '1') {
        setForm((prev) => ({ ...prev, buy_vip: '0' }));
      } else if (switchBuyOpen && form.buy_vip === '0' && canBuyVip) {
        setForm((prev) => ({ ...prev, buy_vip: '1' }));
      }
    }
  };

  // const switchBuyVip = async (buyVip: boolean) => {
  //   SDPlaceOrderPageBtnClick('Free Business Express');
  //   setForm((prev) => {
  //     return {
  //       ...prev,
  //       buy_vip: buyVip ? '1' : '0',
  //     };
  //   });
  //   setSwitchBuyOpen(buyVip);
  //   try {
  //     if (Number(buyVip) === 1) {
  //       trackEvent(
  //         {
  //           eventName: 'VIP_payment',
  //           properties: {
  //             event_label: 'VIP_Express',
  //             event_category: 'payment_list',
  //           },
  //         },
  //         [ProviderType.GA4],
  //       );
  //     }
  //     trackEvent(
  //       {
  //         eventName: 'add_shipping_method',
  //         properties: {
  //           event_label: form.freight_type,
  //           event_category: 'CHECKOUT',
  //         },
  //       },
  //       [ProviderType.GA4],
  //     );
  //   } catch (e) {
  //     logger.log(e);
  //   }

  //   if (shippingMethodValid.paymentMethodError) {
  //     return;
  //   }

  //   const paymentRule1 =
  //     form.wallet === 1 &&
  //     (Number(price.wallet_balance) - grandTotal < 0 || parseFloat(payAmountWithWallet) > 0) &&
  //     !form.payment_type;
  //   const paymentRule2 = form.wallet === 2 && !form.payment_type && !isZero;

  //   // 是否选择支付方式，电子钱包余额不足时是否选择三方支付
  //   if (paymentRule1 || paymentRule2) {
  //     setShippingMethodValid((prev) => ({
  //       ...prev,
  //       paymentMethodError: false,
  //       paymentMethodErrorMessage: 'Please Select Payment Method',
  //     }));
  //     return;
  //   }

  //   // Zing 会员不可使用电子钱包支付
  //   if (form.wallet === 1 && form.buy_vip === '1' && !form.payment_type) {
  //     setShippingMethodValid((prev) => ({
  //       ...prev,
  //       paymentMethodError: false,
  //       paymentMethodErrorMessage:
  //         'Store Credit not available for Zing Members. Select another payment method',
  //     }));
  //   }
  // };

  const continuePayment = () => {
    SDPlaceOrderPageContinuePayBtnClick();
    SDPlaceOrderPageBtnClick('CONTINUE SHOPPING');
    if (!address?.checked) {
      toast.error(t('pleaseEnterSelectYourShippingAddress'));
      return;
    }
    // 校验物流方式是否为空
    if (!form.freight_type) {
      toast.error(t('pleaseSelectShippingMethod'));
      return;
    }

    setStep(STEP.TWO);

    setUserShippingMethod(form.freight_type);
  };

  const backToAddress = () => {
    setStep(STEP.ONE);
    setUserShippingMethod('');
  };

  const onEditOrAdd = (val: boolean) => {
    setOnlyShowAddress(val);
  };

  const onUseWallet = async (isUseWallet: IUseWallet) => {
    setForm((prev) => {
      return {
        ...prev,
        wallet: isUseWallet,
      };
    });
    SDPlaceOrderPageBtnClick('wallet');
    isUseWallet === 1 && SDPlaceOrderPagePayMethodBtnClick('wallet');
  };

  useEffect(() => {
    // 当allUsingWallet变化时，执行相应操作
    if (allUsingWallet) {
      setForm((prev) => {
        return {
          ...prev,
          payment_type: '',
        };
      });
    }
  }, [allUsingWallet]);

  const onChangePaymentMethod = (payment: string) => {
    setShippingMethodValid({
      ...shippingMethodValid,
      paymentMethodError: true,
      paymentMethodErrorMessage: '',
    });

    setForm((prev) => {
      return {
        ...prev,
        payment_type: payment,
      };
    });
  };

  const handleCouponData = async () => {
    const params = {
      cart_ids: cartIdArray,
      currency: currency.name,
    };
    const response = await getOrderCoupon(params);

    if (!response || !response.data) {
      // 处理API返回为空的情况
      return { list: [], disqualification: [] };
    }

    const { data } = response;
    setCouponList(data.list || []);

    if (data.disqualification && Array.isArray(data.disqualification)) {
      data.disqualification.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.canuse = false;
      });
    }

    if (data.list && Array.isArray(data.list)) {
      data.list.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.canuse = true;
      });
    }

    setCanUseLength(data.list ? data.list.length : 0);
    setUsableCouponList((data.list || []).concat(data.disqualification || []));

    return data;
  };

  const getCouponList = async () => {
    const data = await handleCouponData();
    SDPlaceOrderPageCouponResult(!!(data.list && data.list.length));
    if (data.list && data.list.length !== 0) {
      setDiscountPrice(data.list[0].code_discounts_total_current_price);
    }
  };

  const couponApply = async (item: any) => {
    SDPlaceOrderPageCouponSelectClick({
      coupon_id: `${item.discount_code.id}`,
      coupon_name: item.discount_code.name,
      btn_name: 'Apply',
    });
    SDPlaceOrderPageBtnClick('Apply');

    // 保存当前状态用于回滚
    const previousState = {
      code: form.code,
      coupon: form.coupon,
      point: form.point,
    };

    setForm((prev) => ({
      ...prev,
      code: item.code,
      coupon: item.id,
      point: 0,
    }));

    // 计算item 的文案
    if (codeCouponRef.current) {
      codeCouponRef.current.updateState({
        applyLoading: true,
      });
    }

    if (applyPointsRef.current) {
      applyPointsRef.current.updateState({
        checked: false,
      });
    }

    try {
      setIsGlobalLoading(true);
      const res = await getPrice({ code: item.code, coupon: item.id, point: 0 });
      if (res && (res.code === 400200 || res.code === 500)) {
        // 接口失败：回滚所有状态
        setForm((prev) => ({
          ...prev,
          code: previousState.code,
          coupon: previousState.coupon,
          point: previousState.point,
        }));

        // 回滚积分兑换状态
        if (applyPointsRef.current) {
          applyPointsRef.current.updateState({
            checked: previousState.point === 1,
          });
        }

        if (codeCouponRef.current) {
          codeCouponRef.current.updateState({
            couponCode: '',
          });
        }
        toast.error(res.msg);
      } else {
        toast.success(t('couponAppliedSuccessfully'));
        // 滑动到最底部展示出价格
        rightScrollFunc();
        const code = item.code || '';
        const name = (item.discount_code && item.discount_code.name) || '';
        trackEvent(
          {
            eventName: 'click_coupon',
            properties: {
              event_label: `Coupon_code::${code}_${name}`,
              event_category: 'ADD_CART',
            },
          },
          [ProviderType.GA4],
        );

        if (codeCouponRef.current) {
          codeCouponRef.current.uploadCouponText(item);
          codeCouponRef.current.updateState({
            checkCoupon: true,
            applyFlag: true,
          });
        }
      }
    } catch {
      // 异常情况：回滚所有状态
      setForm((prev) => ({
        ...prev,
        code: previousState.code,
        coupon: previousState.coupon,
        point: previousState.point,
      }));

      // 回滚积分兑换状态
      if (applyPointsRef.current) {
        applyPointsRef.current.updateState({
          checked: previousState.point === 1,
        });
      }

      toast.error(t('failedToApplyCoupon'));
    } finally {
      if (codeCouponRef.current) {
        codeCouponRef.current.updateState({
          applyLoading: false,
        });
      }
      setIsGlobalLoading(false);
    }
  };

  const rightScrollFunc = () => {
    setTimeout(() => {
      rightScrollRef.current?.scrollTo({
        top: rightScrollRef.current.scrollHeight - 150,
        behavior: 'smooth',
      });
    }, 100);
  };

  const codeApply = async (code: string) => {
    // 设置加载状态
    SDPlaceOrderPageBtnClick('Apply');
    SDPlaceOrderPageCouponCodeSelectClick({
      coupon_code: code,
      btn_name: 'Apply',
    });

    // 保存当前状态用于回滚
    const previousState = {
      code: form.code,
      coupon: form.coupon,
      point: form.point,
    };

    if (codeCouponRef.current) {
      codeCouponRef.current.updateState({
        applyLoading: true,
      });
    }

    if (applyPointsRef.current) {
      applyPointsRef.current.updateState({
        checked: false,
      });
    }

    setForm((prev) => ({
      ...prev,
      code,
      coupon: 0,
      point: 0,
    }));

    try {
      setIsGlobalLoading(true);
      const res = await getPrice({ code, coupon: 0, point: 0 });
      if (res && (res.code === 400200 || res.code === 500)) {
        // 接口失败：回滚所有状态
        setForm((prev) => ({
          ...prev,
          code: previousState.code,
          coupon: previousState.coupon,
          point: previousState.point,
        }));

        // 回滚积分兑换状态
        if (applyPointsRef.current) {
          applyPointsRef.current.updateState({
            checked: previousState.point === 1,
          });
        }

        if (codeCouponRef.current) {
          codeCouponRef.current.updateState({
            couponCode: '',
          });
        }
        toast.error(res.msg);
      } else {
        toast.success(t('couponAppliedSuccessfully'));
        // 滑动到最底部展示出价格
        rightScrollFunc();
        if (codeCouponRef.current) {
          codeCouponRef.current.updateState({
            checkCoupon: true,
            applyFlag: true,
          });
        }
      }
    } catch {
      // 异常情况：回滚所有状态
      setForm((prev) => ({
        ...prev,
        code: previousState.code,
        coupon: previousState.coupon,
        point: previousState.point,
      }));

      // 回滚积分兑换状态
      if (applyPointsRef.current) {
        applyPointsRef.current.updateState({
          checked: previousState.point === 1,
        });
      }

      toast.error(t('failedToApplyCoupon'));
    } finally {
      // 无论成功失败，都重置加载状态
      if (codeCouponRef.current) {
        codeCouponRef.current.updateState({
          applyLoading: false,
        });
      }
      setIsGlobalLoading(false);
    }
  };

  const applyCancel = async () => {
    SDPlaceOrderPageBtnClick('Cancel');

    if (form.coupon) {
      SDPlaceOrderPageCouponSelectClick({
        btn_name: 'Cancel',
      });
    } else if (form.code) {
      SDPlaceOrderPageCouponCodeSelectClick({
        btn_name: 'Cancel',
      });
    }
    setForm((prev) => ({
      ...prev,
      code: '',
      coupon: 0,
    }));

    try {
      setIsGlobalLoading(true);
      await getPrice({ code: '', coupon: 0 });
    } catch {
      toast.error(t('failedToApplyCoupon'));
    } finally {
      setIsGlobalLoading(false);
    }

    if (codeCouponRef.current) {
      codeCouponRef.current.updateState({
        checkCoupon: false,
        applyLoading: false,
        couponCode: '',
        couponCodeText: '',
      });
    }
  };

  const usePoints = async (isUse: boolean) => {
    SDPlaceOrderPagePointSelectClick();
    setForm((prev) => ({
      ...prev,
      point: isUse ? 1 : 0,
    }));
  };

  const goCart = () => {
    if (!redeemCartId) {
      router.push('/cart?from=payment');
    } else {
      router.push('/account/points-mall');
    }
  };
  //
  const joinFreeSuccess = () => {
    sessionStorage.setItem('zl_payment_join_free_uuid', paymentUuid);
    reloadPage();
  };

  const reloadPage = () => {
    setIsGlobalLoading(true);
    window.location.reload();
    setTimeout(() => {
      setIsGlobalLoading(false);
    }, 10 * 1000);
  };

  const changeDelivery = (_isCheckDelivery: number) => {
    setIsCheckDelivery(_isCheckDelivery);
    form.wallet === 1 && getPrice({ is_insurance: _isCheckDelivery });
    SDPlaceOrderPageSeelBtnClick(_isCheckDelivery);
  };

  const getGrantTotal = useCallback((total: number | string) => {
    setGrandTotal(Number(total));
    logger.log('total', total);
    if (total === '0.00') {
      setIsZero(true);
    } else {
      setIsZero(false);
    }
  }, []);

  const handleCloseRetainPopup = async () => {
    setRetainDialogShow(false);
    if (!deepType) {
      await handleCouponData();
    }
  };

  const applyRetainCoupon = (item) => {
    couponApply(item);
  };

  const applyPopCoupon = async () => {
    if (codeCouponRef.current) {
      codeCouponRef.current.updateState({
        applyLoading: true,
      });
    }

    try {
      const data = await handleCouponData();
      applyRetainCoupon(data.list[0]);
    } catch (error) {
      logger.log(error);
    }
  };

  // 埋点code --------- start

  /**
   * @TrackType sensorsdata
   * @EventType - PlaceOrderPageCoupon_Result
   * @EventDesc -
   * @EventParams {
   *  payment_view_uuid
   *  is_available_coupon
   *  entrance_payment_scene
   * }
   * @Tapd 1008875
   */
  const SDPlaceOrderPageCouponResult = (is_available_coupon) => {
    try {
      const sdEventName = 'PlaceOrderPageCoupon_Result';
      const sdEventParams = {
        payment_view_uuid: paymentUuid,
        entrance_payment_scene: entrancePaymentScene.current,
        is_available_coupon,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType - PlaceOrderPagePlaceOrderBtn_Click
   * @EventDesc - 支付按钮点击
   * @EventParams {
   *  entrance_payment_scene 进入支付页场景
   *  shipping_method 物流方式
   *  pay_type 支付方式
   *  pay_method 支付方法
   *  electronic_wallet_payment_amount 电子钱包支付金额
   *  is_use_coupon 是否使用优惠券
   *  is_use_code 是否使用优惠码
   *  is_use_points 是否使用积分
   *  order_amount: 订单金额
   *  shipping_amount 物流金额
   *  free_shipping_discount_amount 免费物流折扣金额
   *  estimated_taxes 预估税
   *  points_discount_amount 积分折扣金额
   *  coupon_discount_amount 优惠券折扣金额
   *  second_halfprice_discount_amount 第二件半价折扣金额
   *  vip_amount vip金额
   *  is_display_cargo_insurance 是否显示物流保险服务
   *  is_use_cargo_insurance 是否勾选物流保险服务
   *  cargo_insurance_amount 包裹保险金额
   * }
   * @Tapd 1008875,1010351
   */
  const SDPlaceOrderPagePlaceOrderBtnClick = () => {
    const sdEventName = 'PlaceOrderPagePlaceOrderBtn_Click';
    const priceObj = orderSummaryRef.current.SDGetPrice();
    const pay_method = form.payment_type || 'wallet';

    const campaign_type_id_list = [];
    const campaign_sku_list = [];

    const carts = productListRef.current?.selectedListFormPriceApi || [];
    Array.isArray(carts) &&
      carts.forEach((item: any) => {
        if (item.buried_point_data?.campaign_type_id_list?.length > 0) {
          campaign_type_id_list.push(...item.buried_point_data.campaign_type_id_list);
        }
        if (item.buried_point_data?.campaign_sku_list?.length > 0) {
          campaign_sku_list.push(...item.buried_point_data.campaign_sku_list);
        }
      });

    // 数组去重
    const uniqueCampaignTypeIdList = Array.from(new Set(campaign_type_id_list));
    const uniqueCampaignSkuList = Array.from(new Set(campaign_sku_list));
    // 是否包邮（触发包邮政策物流费用为 0 时，此项为是，否则为否；包邮政策不含 zing 会员、物流券）
    const _shippingMethod = shippingMethod || {};
    const { freight_discount_type: discountType } = _shippingMethod;
    const _price = _shippingMethod.price;
    let is_free_shipping = false;
    if (_price === '0.00' && ![3, 4].includes(discountType)) {
      is_free_shipping = true;
    }

    try {
      const payMethodItem = paymentTypeList?.find((item) => item.payment_method === pay_method);
      const sdEventParams = {
        payment_view_uuid: paymentUuid,
        entrance_payment_scene: entrancePaymentScene.current,
        shipping_method: form.freight_type,
        pay_method: payMethodItem.payment_method,
        pay_channel: payMethodItem.payment_channel,
        pay_type: payMethodItem.payment_type,
        electronic_wallet_payment_amount: priceObj.electronic_wallet_payment_amount,
        is_use_coupon: !!form.coupon,
        is_use_code: !!form.coupon && !!form.code,
        is_use_points: form.point === 1,
        order_amount: priceObj.order_amount,
        shipping_amount: priceObj.shipping_amount,
        free_shipping_discount_amount: priceObj.free_shipping_discount_amount,
        estimated_taxes: priceObj.estimated_taxes,
        points_discount_amount: form.point === 1 ? priceObj.points_discount_amount : '',
        coupon_discount_amount: priceObj.coupon_discount_amount,
        second_halfprice_discount_amount: priceObj.second_halfprice_discount_amount,
        vip_amount: priceObj.vip_amount,
        campaign_sku_list: uniqueCampaignSkuList,
        campaign_type_id_list: uniqueCampaignTypeIdList,
        is_free_shipping,
        is_display_cargo_insurance: +abFreeDeliveryVariable !== 1 && +cargoInsuranceAmount > 0,
        is_use_cargo_insurance: +isCheckDelivery === 1 && +cargoInsuranceAmount > 0,
        cargo_insurance_amount: +cargoInsuranceAmount > 0 ? cargoInsuranceAmount : undefined,
      };

      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.log(error);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType PlaceOrderPagePlaceOrder_Result
   * @EventDesc 支付订单页placeorder按钮点击
   * @EventParams {
   *  payment_view_uuid: payment_view_uuid
   *  order_no: 订单号
   *  shipping_method: 物流方式
   *  pay_type: 支付方式
   *  is_use_coupon: 是否使用优惠券/优惠码
   *  is_use_code: 是否使用优惠码
   *  is_use_points: 是否使用积分
   *  order_amount: 订单金额
   *  shipping_amount: 物流金额
   *  free_shipping_discount_amount: 免费物流折扣金额
   *  estimated_taxes: 预估税
   *  points_discount_amount: 积分折扣金额
   *  coupon_discount_amount: 优惠券折扣金额
   *  second_halfprice_discount_amount: 第二件半价折扣金额
   *  vip_amount: vip金额
   *  is_create_success: 是否创建成功
   *  service_code: 服务端状态码
   *  is_display_cargo_insurance 是否显示物流保险服务
   *  is_use_cargo_insurance 是否勾选物流保险服务
   *  cargo_insurance_amount 包裹保险金额
   * }
   * @Tapd 1008875
   */
  const SDPlaceOrderPagePlaceOrderResult = (
    order_no: string,
    is_create_success: boolean,
    service_code: string | number,
    msg?: string,
  ) => {
    setIsCreateOrderSuccess(is_create_success);
    const sdEventName = 'PlaceOrderPagePlaceOrder_Result';
    const priceObj = orderSummaryRef.current.SDGetPrice();
    const pay_method = form.payment_type || 'wallet';
    const payMethodItem = paymentTypeList?.find((item) => item.payment_method === pay_method);
    const campaign_type_id_list = [];
    const campaign_sku_list = [];
    const carts = productListRef.current?.selectedListFormPriceApi || [];
    Array.isArray(carts) &&
      carts.forEach((item: any) => {
        if (item.buried_point_data?.campaign_type_id_list?.length > 0) {
          campaign_type_id_list.push(...item.buried_point_data.campaign_type_id_list);
        }
        if (item.buried_point_data?.campaign_sku_list?.length > 0) {
          campaign_sku_list.push(...item.buried_point_data.campaign_sku_list);
        }
      });

    // 数组去重
    const uniqueCampaignTypeIdList = Array.from(new Set(campaign_type_id_list));
    const uniqueCampaignSkuList = Array.from(new Set(campaign_sku_list));

    // 是否包邮（触发包邮政策物流费用为 0 时，此项为是，否则为否；包邮政策不含 zing 会员、物流券）
    const _shippingMethod = shippingMethod || {};
    const { freight_discount_type: discountType } = _shippingMethod;
    const _price = _shippingMethod.price;
    let is_free_shipping = false;
    if (_price === '0.00' && ![3, 4].includes(discountType)) {
      is_free_shipping = true;
    }

    // 根据 service_code 设置对应的 msg 值
    const serviceCodeMsgMap = {
      '00401': 'no_shipping',
      '00402': 'shippingError',
      '00403': 'no_shipping_method',
      '00404': 'no_payment_method',
      '00405': 'no_billing',
      '00406': 'billingError',
      '00407': 'no_save_shipping',
      '00408': 'no_save_shipping_method',
      '00409': 'no_save_billing',
      '00410': 'no_agree',
      '00411': 'address_has_delete',
      '00412': 'address_invalid',
    };

    const mappedMsg = serviceCodeMsgMap[`${service_code}`] || msg;

    const sdEventParams = {
      payment_view_uuid: paymentUuid,
      entrance_payment_scene: entrancePaymentScene.current,
      order_no: `${order_no}`,
      shipping_method: form.freight_type,

      pay_method: payMethodItem.payment_method,
      pay_channel: payMethodItem.payment_channel,
      pay_type: payMethodItem.payment_type,
      is_use_coupon: !!form.coupon,
      electronic_wallet_payment_amount: priceObj.electronic_wallet_payment_amount,
      is_use_code: !!form.coupon && !!form.code,
      is_use_points: form.point === 1,
      order_amount: priceObj.order_amount,
      shipping_amount: priceObj.shipping_amount,
      free_shipping_discount_amount: priceObj.free_shipping_discount_amount,
      estimated_taxes: priceObj.estimated_taxes,
      points_discount_amount: form.point === 1 ? priceObj.points_discount_amount : '',
      coupon_discount_amount: priceObj.coupon_discount_amount,
      second_halfprice_discount_amount: priceObj.second_halfprice_discount_amount,
      vip_amount: priceObj.vip_amount,
      is_create_success,
      service_code: `${service_code}`,
      campaign_sku_list: uniqueCampaignSkuList,
      campaign_type_id_list: uniqueCampaignTypeIdList,
      is_free_shipping,
      is_display_cargo_insurance: +abFreeDeliveryVariable !== 1 && +cargoInsuranceAmount > 0,
      is_use_cargo_insurance: +isCheckDelivery === 1 && +cargoInsuranceAmount > 0,
      cargo_insurance_amount: +cargoInsuranceAmount > 0 ? cargoInsuranceAmount : undefined,
      msg: mappedMsg,
    };

    trackEvent(
      {
        eventName: sdEventName,
        properties: sdEventParams,
      },
      [ProviderType.SENSORS],
    );
  };

  /**
   * @TrackType sensorsdata
   * @EventType PlaceOrderPageBtn_Click
   * @EventDesc 支付订单页按钮点击
   * @EventParams {
   *  payment_view_uuid: payment_view_uuid,
   *  btn_name: 按钮名称
   * }
   * @Tapd 1008875
   */

  const SDPlaceOrderPageBtnClick = (btn_name: string) => {
    if (!btn_name) return;
    try {
      const eventData = {
        eventName: 'PlaceOrderPageBtn_Click',
        properties: {
          btn_name,
          payment_view_uuid: paymentUuid,
          entrance_payment_scene: entrancePaymentScene.current,
        },
      };
      trackEvent(eventData, [ProviderType.SENSORS]);
    } catch (e) {
      logger.log(e);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType - PlaceOrderPage_Leave
   * @EventDesc - 页面离开
   * @EventParams {
   *  payment_view_uuid payment_view_uuid
   *  entrance_payment_scene 进入支付页场景
   * }
   * @Tapd 1008875
   */
  const SDPlaceOrderPageLeave = () => {
    if (!isSDPlaceOrderPageViewSend || isCreateOrderSuccess) return;
    try {
      const sdEventName = 'PlaceOrderPage_Leave';
      const view_duration = Math.ceil((Date.now() - startTime) / 1000);
      const sdEventParams = {
        refer_page_name: fromRouterInfo.current.refer_page_name || '',
        refer_url_path: fromRouterInfo.current.refer_url_path || '',
        payment_view_uuid: paymentUuid,
        entrance_payment_scene: entrancePaymentScene.current,
        cart_product_ids: selectedCart.map((id) => `${id}`),
        view_duration,
      };

      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType - PlaceOrderPagePayMethodBtn_Click
   * @EventDesc - 支付页面支付按钮点击事件
   * @EventParams {
   *  pay_method 支付方式（取支付接口的method字段）
   *  entrance_payment_scene 进入支付页场景
   * }
   * @Tapd 1010351
   */
  const SDPlaceOrderPagePayMethodBtnClick = (pay_method: string) => {
    try {
      const payMethodItem = paymentTypeList?.find((item) => item.payment_method === pay_method);
      const sdEventName = 'PlaceOrderPagePayMethodBtn_Click';
      const sdEventParams = {
        pay_method: payMethodItem.payment_method,
        pay_channel: payMethodItem.payment_channel,
        pay_type: payMethodItem.payment_type,
        entrance_payment_scene: entrancePaymentScene.current,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType - PlaceOrderPageShipMethodBtn_Click
   * @EventDesc - 支付页面物流按钮点击事件
   * @EventParams {
   *  ship_method 物流方式（服务端返回的接口名称
   *  entrance_payment_scene 进入支付页场景
   * }
   * @Tapd 1010351
   */
  const SDPlaceOrderPageShipMethodBtnClick = (shipping_method: string) => {
    try {
      const sdEventParams = {
        shipping_method,
        entrance_payment_scene: entrancePaymentScene.current,
      };
      trackEvent(
        {
          eventName: 'PlaceOrderPageShipMethodBtn_Click',
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType - PlaceOrderPageContinuePayBtn_Click
   * @EventDesc - 支付订单页积分使用选择点击
   * @EventParams {
   *  payment_view_uuid payment_view_uuid
   *  entrance_payment_scene 进入支付页场景
   *  shipping_method 物流方式（服务端返回的接口名称）
   * }
   * @Tapd 1008875
   */
  const SDPlaceOrderPageContinuePayBtnClick = () => {
    try {
      const sdEventName = 'PlaceOrderPageContinuePayBtn_Click';
      const sdEventParams = {
        payment_view_uuid: paymentUuid,
        entrance_payment_scene: entrancePaymentScene.current,
        shipping_method: form.freight_type,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType - PlaceOrderPageCouponSelect_Click
   * @EventDesc - 支付订单页优惠券选择点击
   * @EventParams {
   *  payment_view_uuid payment_view_uuid
   *  entrance_payment_scene 进入支付页场景
   *  coupon_id 优惠券ID
   *  coupon_name 优惠券名称
   *  btn_name 按钮名称
   * }
   * @Tapd 1008875
   */
  const SDPlaceOrderPageCouponSelectClick = (params) => {
    try {
      const sdEventName = 'PlaceOrderPageCouponSelect_Click';
      const sdEventParams = {
        payment_view_uuid: paymentUuid,
        entrance_payment_scene: entrancePaymentScene.current,
        ...params,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType - PlaceOrderPageSeelBtn_Click
   * @EventDesc - 物流保险按钮点击
   * @EventParams {
   *  payment_view_uuid payment_view_uuid
   *  entrance_payment_scene 进入支付页场景
   *  cargo_insurance_amount 物流保险金额
   *  currency 币种
   *  is_default_check 是否默认勾选中
   *  btn_name 按钮名称
   * }
   * ZELL-58224
   */
  const SDPlaceOrderPageSeelBtnClick = (btn_type = +abFreeDeliveryVariable === 2 ? 1 : 2) => {
    // abFreeDeliveryVariable为1说明是对照组，不显示物流保险，不触发事件
    if (+abFreeDeliveryVariable === 1 || +cargoInsuranceAmount <= 0) return;
    const sdEventName = 'PlaceOrderPageSeelBtn_Click';
    const sdEventParams = {
      payment_view_uuid: paymentUuid,
      entrance_payment_scene: entrancePaymentScene.current,
      cargo_insurance_amount: cargoInsuranceAmount,
      currency: currency.name,
      is_default_check: +abFreeDeliveryVariable === 2,
      btn_name: btn_type === 1 ? 'Apply' : 'Cancel',
    };
    trackEvent(
      {
        eventName: sdEventName,
        properties: sdEventParams,
      },
      [ProviderType.SENSORS],
    );
  };

  /**
   * @TrackType sensorsdata
   * @EventType - PlaceOrderPageCouponCodeSelect_Click
   * @EventDesc - 支付订单页优惠码选择点击
   * @EventParams {
   *  payment_view_uuid payment_view_uuid
   *  entrance_payment_scene 进入支付页场景
   *  coupon_code 优惠码
   *  btn_name 按钮名称
   * }
   * @Tapd 1008875
   */
  const SDPlaceOrderPageCouponCodeSelectClick = (params) => {
    try {
      const sdEventName = 'PlaceOrderPageCouponCodeSelect_Click';
      const sdEventParams = {
        payment_view_uuid: paymentUuid,
        entrance_payment_scene: entrancePaymentScene.current,
        ...params,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType - PlaceOrderPagePointSelect_Click
   * @EventDesc - 支付订单页积分使用选择点击
   * @EventParams {
   *  payment_view_uuid payment_view_uuid
   *  entrance_payment_scene 进入支付页场景
   *  use_point 使用积分
   *  points_discount_amount 优惠码
   *  currency 币种
   *  btn_name 按钮名称
   * }
   * @Tapd 1008875
   */
  const SDPlaceOrderPagePointSelectClick = () => {
    try {
      const sdEventName = 'PlaceOrderPagePointSelect_Click';
      const priceObj = orderSummaryRef.current.SDGetPrice();
      const sdEventParams = {
        payment_view_uuid: paymentUuid,
        entrance_payment_scene: entrancePaymentScene.current,
        currency: currency.name,
        points_discount_amount: form.point === 0 ? priceObj.points_discount_amount : '',
        use_point: form.point === 0 ? price.expend_point : '',
        btn_name: form.point === 0 ? 'Apply' : 'Cancel',
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  /**
   * @TrackType sensorsdata
   * @EventType PlaceOrderPage_View
   * @EventDesc 支付订单页面浏览
   * @EventParams {
   *  payment_view_uuid: payment_view_uuid,
   *  entrance_payment_scene: 进入支付页场景,
   *  cart_product_ids: 购物车商品id
   * }
   * @Tapd 1008875
   */
  const SDPlaceOrderPageView = () => {
    try {
      if (isSDPlaceOrderPageViewSend) return;
      setIsSDPlaceOrderPageViewSend(true);
      const sdEventName = 'PlaceOrderPage_View';
      const sdEventParams = {
        refer_page_name: fromRouterInfo.current.refer_page_name || '',
        refer_url_path: fromRouterInfo.current.refer_url_path || '',
        payment_view_uuid: paymentUuid,
        entrance_payment_scene: entrancePaymentScene.current,
        cart_product_ids: selectedCart.map((id) => `${id}`),
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  /**
   * 进入 payment 页面 GA 统计事件
   */
  const sendBeginCheckoutGA = () => {
    try {
      const arr = [];
      selectedCart.forEach((item) => {
        canBuyList.forEach((cartItem) => {
          cartItem.carts.forEach((productItem) => {
            // 如果当前品是普通品，并且属于秒杀，那么状态改为 6，为了展示对应的商品条目标签
            // 6 表示秒杀
            if (item === productItem.cart_id) {
              const curr: any = JSON.parse(JSON.stringify(productItem));
              if (curr.type === 1 && curr.product.is_seckill === 1) {
                curr.type = 6;
              }
              arr.push(curr);
            }
          });
        });
      });
      const tempGtagItems = arr.map((item) => {
        const finalPrice = item.product.price_info
          .find((curr) => curr.currency === currency.name)
          .sell_price.replace(/[^\d.]/g, '');
        const oldPrice = item.product.price_info
          .find((curr) => curr.currency === currency.name)
          .price.replace(/[^\d.]/g, '');
        return {
          item_id: item.product.shop_sku,
          item_name: item.product.short_name,
          discount: (oldPrice - finalPrice).toFixed(2),
          item_category: item.product.shop_sku === 'Box' ? 'Box' : item.option.prescription_type,
          item_variant: item.product.color,
          price: finalPrice,
          currency: currency.name,
          quantity: item.qty,
        };
      });
      setGtagItems(tempGtagItems);

      // TODO: 有个报错！
      const discount = price.discount ? price.discount.replace(/[^\d.]/g, '') : 0;
      trackEvent(
        {
          eventName: 'begin_checkout',
          properties: {
            coupon: discount,
            currency: currency.name,
            value: grandTotal,
            items: gtagItems,
          },
        },
        [ProviderType.GA4],
      );
    } catch (e) {
      logger.log(e);
    }
  };

  const sendGAplaceOrderClick = () => {
    const arr = [];
    selectedCart.forEach((item) => {
      canBuyList.forEach((val: any) => {
        if (item === val.cart_id) {
          arr.push(val);
        }
      });
    });

    trackEvent(
      {
        eventName: 'payment_method',
        properties: {
          event_label: form.payment_type,
          event_category: 'CHECKOUT',
          productId: arr.map((i) => i.product.shop_sku).join(','),
          productName: arr.map((i) => i.product.short_name).join(','),
          productQuantity: gtagItems.map((i) => i.quantity).join(','),
          productPrice: arr.map((i) => i.final_price).join(','),
          productVariant: arr.map((i) => `${i.product.color}_${i.product.size}`).join(','),
        },
      },
      [ProviderType.GA4],
    );
  };

  return (
    <div className="bg-[#f0f0f0] md:bg-[#f5f6f7]  md:mx-auto md:pb-[4px] md:w-9/10 xl:w-4/5">
      {!isMobile && (
        <section className="flex justify-between box-border pb-[60px]">
          {/* 第一步 */}
          <section
            style={{ display: step === STEP.ONE ? 'block' : 'none' }}
            className="min-w-[740px] w-[920px]"
          >
            <section className="md:rounded-[5px] md:bg-white md:py-[16px] 2xl:py-[20px] md:px-[26px]">
              <AddressSection
                ref={addressRef}
                // addressList={addressList}
                entrancePaymentScene={entrancePaymentScene.current}
                paymentUuid={paymentUuid}
                onAddressChange={onAddressChange}
                onEditOrAdd={onEditOrAdd}
                refer_page_name={fromRouterInfo.current.refer_page_name || ''}
                refer_url_path={fromRouterInfo.current.refer_url_path || ''}
              />
            </section>
            <section
              style={{ display: onlyShowAddress ? 'none' : 'block' }}
              className="md:mt-[12px] md:rounded-[5px] md:bg-white md:py-[16px] 2xl:py-[20px] md:px-[26px]"
            >
              <section className="flex flex-col ">
                <div className="">
                  <div className="text-[18px] font-bb text-primary-black">
                    {t('shippingMethod')}
                  </div>
                  <div className="text-[12px] font-br text-secondary-text mt-10">
                    {t('deliveryTimeIncludes')}
                  </div>
                </div>
                <div className="flex flex-col md:flex-row">
                  <ShippingMethod
                    freightList={freightList}
                    // countryStatus={address?.status}
                    isPobox={address?.boxChecked}
                    isVip={isVip}
                    isMobile={isMobile}
                    changeShippingMethod={changeShippingMethod}
                    address={address}
                    onTrackEvent={(btn_name: string) => {
                      SDPlaceOrderPageBtnClick(btn_name);
                      SDPlaceOrderPageShipMethodBtnClick(btn_name);
                    }}
                  />
                  <div className="ml-[40px]">
                    {/* {allowBuyVip && vipEquity && (
                      <BuyVip
                        vipEquity={vipEquity}
                        switchBuyVip={switchBuyVip}
                        initialBuyVip={switchBuyOpen}
                      />
                    )} */}
                  </div>
                </div>
              </section>
            </section>
            {!isZero && step === STEP.ONE && !onlyShowAddress && (
              <ContinuePaymentButton continuePayment={continuePayment} />
            )}
          </section>

          {/* 第二步 */}
          <section
            style={{ display: step === STEP.TWO ? 'block' : 'none' }}
            className=" min-w-[740px] w-[920px]"
          >
            <section className="md:rounded-[5px] md:bg-white md:py-[24px] md:px-[30px]">
              <ShippingAddressAndMethod
                address={address}
                method={freightList.find((item) => item.name === userShippingMethod)}
                freightList={freightList}
                onEdit={backToAddress}
              />
            </section>
            <section className="md:flex md:mt-[12px] md:rounded-[5px] md:bg-white md:py-[24px] md:px-[30px] ">
              <section className="md:w-[55%]">
                <PaymentMethod
                  showWallet={showWallet}
                  walletBalance={price?.wallet_balance}
                  loading={loading}
                  setLoading={setLoading}
                  walletPayAmount={walletPayAmount}
                  payAmountWithWallet={payAmountWithWallet}
                  onUseWallet={onUseWallet}
                  allowPayment={allowPayment}
                  paypalBtn="#paypal-btn"
                  adyenDomId="#adyen-container"
                  adyenDom3ds="#adyen-container-3ds"
                  billingAddress={billingAddress}
                  shippingAddress={address}
                  cartIds={cartIdArray}
                  grandTotal={grandTotal}
                  payMethods={allowPayment}
                  paymentTypeList={paymentTypeList}
                  isUseWallet={form.wallet}
                  createOrders={stableCreateOrder}
                  onChangePaymentMethod={onChangePaymentMethod}
                  disabled={allUsingWallet}
                  ref={globalPayRef}
                  lastPayMethod={lastPayMethod}
                  userAgent={userAgent}
                  onPaypalOnClick={() => {
                    SDPlaceOrderPagePlaceOrderBtnClick();
                  }}
                  onPaymentPageClick={(pay_method: string) => {
                    SDPlaceOrderPageBtnClick(pay_method);
                    SDPlaceOrderPagePayMethodBtnClick(pay_method);
                  }}
                />
              </section>
              <section
                className={`md:w-[40%] md:ml-[36px]  min-w-[300px] ${showBillingAddress ? '' : 'hidden'}`}
              >
                <BillingAddress
                  ref={billingAddressRef}
                  entrancePaymentScene={entrancePaymentScene.current}
                  shippingAddress={address}
                  // addressList={addressList}
                  billingAddress={billingAddress}
                  paymentType={form.payment_type}
                  onBillingAddressChange={onBillingAddressChange}
                  onChangeBillFlag={onChangeBillFlag}
                  refer_page_name={fromRouterInfo.current.refer_page_name || ''}
                  refer_url_path={fromRouterInfo.current.refer_url_path || ''}
                />
              </section>
            </section>
          </section>

          {/* 右侧 */}
          <section className="flex-1 ml-[60px] 2xl:ml-[80px] 3xl:ml-[100px] min-w-[360px] flex flex-col gap-12">
            <section
              ref={rightScrollRef}
              className="overflow-y-auto"
              style={{ maxHeight: rightScrollHeight > 0 ? `${rightScrollHeight}px` : 'auto' }}
            >
              <ProductList
                redeemCartId={redeemCartId}
                priceOrder={priceOrder}
                ref={productListRef}
              />

              <WorryFreeDelivery
                freightList={freightList}
                freight={form.freight_type}
                isCheckDelivery={isCheckDelivery}
                changeDelivery={changeDelivery}
              />

              {!redeemCartId && (
                <CodeCoupon
                  ref={codeCouponRef}
                  canUseLength={canUseLength}
                  usableCouponList={usableCouponList}
                  couponApply={couponApply}
                  codeApply={codeApply}
                  applyCancel={applyCancel}
                  price={price}
                />
              )}
              {/* de fr 没有 zing 会员，但是也有积分抵扣功能 */}
              {!redeemCartId && (IS_DE_FR ? true : vipEquity) && (
                <Points
                  ref={applyPointsRef}
                  price={price}
                  vipEquity={vipEquity}
                  showPaymentJoinMember={showPaymentJoinMember}
                  onUsePoints={usePoints}
                  joinFreeSuccess={joinFreeSuccess}
                />
              )}
              {priceOrder && [3, 4].includes(priceOrder.gift_status) && (
                <AnniversaryGift
                  giftStatus={priceOrder.gift_status}
                  giftActivityImage={priceOrder.gift_activity_image}
                />
              )}
              {price && (
                <>
                  <OrderSummary
                    ref={orderSummaryRef}
                    isRedeem={!!redeemCartId}
                    price={price}
                    shippingAddress={address}
                    freightList={freightList}
                    freight={form.freight_type}
                    abShowFreeDelivery={abShowFreeDelivery}
                    isCheckDelivery={isCheckDelivery}
                    isUseWallet={form.wallet}
                    walletPayAmount={walletPayAmount}
                    payAmountWithWallet={payAmountWithWallet}
                    paymentType={form.payment_type}
                    onGetGrantTotal={getGrantTotal}
                  />
                </>
              )}
            </section>

            <section>
              <PlaceOrderButton
                goToPlaceOrder={goToPlaceOrder}
                loading={loading.placeOrder}
                paymentType={form.payment_type}
                placeOrderDisable={placeOrderDisable}
                privacyPolicy={form.privacyPolicy}
                onPrivacyPolicyChange={(checked) => {
                  setForm((prev) => ({
                    ...prev,
                    privacyPolicy: checked,
                  }));
                }}
              />
            </section>
          </section>
        </section>
      )}

      {isMobile && (
        <section className="px-12 pt-[10px] pb-[120px] relative">
          <AddressSection
            ref={addressRef}
            // addressList={addressList}
            entrancePaymentScene={entrancePaymentScene.current}
            paymentUuid={paymentUuid}
            onAddressChange={onAddressChange}
            onEditOrAdd={onEditOrAdd}
            isMobile={isMobile}
            refer_page_name={fromRouterInfo.current.refer_page_name || ''}
            refer_url_path={fromRouterInfo.current.refer_url_path || ''}
          />

          {!redeemCartId && (
            <CodeCoupon
              ref={codeCouponRef}
              canUseLength={canUseLength}
              usableCouponList={usableCouponList}
              couponApply={couponApply}
              codeApply={codeApply}
              applyCancel={applyCancel}
              price={price}
            />
          )}
          {!redeemCartId && (
            <Points
              ref={applyPointsRef}
              price={price}
              vipEquity={vipEquity}
              showPaymentJoinMember={showPaymentJoinMember}
              onUsePoints={usePoints}
              joinFreeSuccess={joinFreeSuccess}
            />
          )}

          <WorryFreeDelivery
            freightList={freightList}
            freight={form.freight_type}
            isCheckDelivery={isCheckDelivery}
            changeDelivery={changeDelivery}
          />

          {/* 物流方式。 注意： error-section 被引用了 */}
          <section
            className={`mt-10 p-10 px-[6px] bg-white rounded-lg ${
              !shippingMethodValid.shippingMethodError
                ? 'border-1 border-[#BC1B1B] error-section'
                : ''
            }`}
          >
            <div className="text-[16px] font-bb text-primary-black">{t('shippingMethod')}</div>
            <div className="text-[12px] font-br text-secondary-text mt-10">
              {t('deliveryTimeIncludes')}
            </div>
            <ShippingMethod
              freightList={freightList}
              // countryStatus={address?.status}
              isPobox={address?.boxChecked}
              isVip={isVip}
              address={address}
              isMobile={isMobile}
              changeShippingMethod={changeShippingMethod}
              onTrackEvent={(btn_name: string) => {
                SDPlaceOrderPageBtnClick(btn_name);
                SDPlaceOrderPageShipMethodBtnClick(btn_name);
              }}
            />
            {/* {allowBuyVip && (
              <BuyVip
                vipEquity={vipEquity}
                switchBuyVip={switchBuyVip}
                initialBuyVip={switchBuyOpen}
              />
            )} */}

            {!shippingMethodValid.shippingMethodError && (
              <div className="text-[#BC1B1B] text-12 mt-10">
                {shippingMethodValid.shippingMethodErrorMessage}
              </div>
            )}
          </section>

          {/* 支付方式。 注意： error-section 被引用了 */}
          <section
            className={`mt-10 p-10 px-[6px] bg-white rounded-lg ${
              !shippingMethodValid.paymentMethodError
                ? 'border-1 border-[#BC1B1B] error-section'
                : ''
            }`}
          >
            <PaymentMethod
              showWallet={showWallet}
              walletBalance={price?.wallet_balance}
              loading={loading}
              setLoading={setLoading}
              walletPayAmount={walletPayAmount}
              payAmountWithWallet={payAmountWithWallet}
              onUseWallet={onUseWallet}
              allowPayment={allowPayment}
              paypalBtn="#paypal-btn"
              adyenDomId="#adyen-container"
              adyenDom3ds="#adyen-container-3ds"
              billingAddress={billingAddress}
              shippingAddress={address}
              cartIds={cartIdArray}
              grandTotal={grandTotal}
              payMethods={allowPayment}
              paymentTypeList={paymentTypeList}
              isUseWallet={form.wallet}
              createOrders={stableCreateOrder}
              onChangePaymentMethod={onChangePaymentMethod}
              disabled={allUsingWallet}
              ref={globalPayRef}
              userAgent={userAgent}
              lastPayMethod={lastPayMethod}
              onPaypalOnClick={() => {
                SDPlaceOrderPagePlaceOrderBtnClick();
              }}
              onPaymentPageClick={(pay_method: string) => {
                SDPlaceOrderPageBtnClick(pay_method);
                SDPlaceOrderPagePayMethodBtnClick(pay_method);
              }}
            />

            {!shippingMethodValid.paymentMethodError && (
              <div className="text-[#BC1B1B] text-12 mt-10">
                {shippingMethodValid.paymentMethodErrorMessage}
              </div>
            )}
          </section>

          {/* 账单地址。 注意： error-section 被引用了 */}
          <section
            className={`bg-white py-18 px-8 rounded-lg mt-10 border ${
              !shippingMethodValid.billingAddressError
                ? 'border-[#BC1B1B] error-section'
                : 'border-transparent'
            } ${showBillingAddress ? '' : 'hidden'}`}
          >
            <BillingAddress
              ref={billingAddressRef}
              entrancePaymentScene={entrancePaymentScene.current}
              shippingAddress={address}
              // addressList={addressList}
              refer_page_name={fromRouterInfo.current.refer_page_name || ''}
              refer_url_path={fromRouterInfo.current.refer_url_path || ''}
              billingAddress={billingAddress}
              paymentType={form.payment_type}
              onBillingAddressChange={onBillingAddressChange}
              onChangeBillFlag={onChangeBillFlag}
            />

            {!shippingMethodValid.billingAddressError && (
              <div className="text-[#BC1B1B] text-12 mt-10">
                {shippingMethodValid.billingAddressErrorMessage}
              </div>
            )}
          </section>
          <section className="mt-10 p-[18px_12px] bg-white rounded-lg">
            <ProductList redeemCartId={redeemCartId} priceOrder={priceOrder} ref={productListRef} />
          </section>
          {price && (
            <OrderSummary
              ref={orderSummaryRef}
              isRedeem={!!redeemCartId}
              price={price}
              shippingAddress={address}
              freightList={freightList}
              freight={form.freight_type}
              abShowFreeDelivery={abShowFreeDelivery}
              isCheckDelivery={isCheckDelivery}
              isUseWallet={form.wallet}
              walletPayAmount={walletPayAmount}
              payAmountWithWallet={payAmountWithWallet}
              paymentType={form.payment_type}
              onGetGrantTotal={getGrantTotal}
            />
          )}

          <RightsImageWithAgreement
            privacyPolicy={form.privacyPolicy}
            onPrivacyPolicyChange={(checked) => {
              setForm((prev) => ({
                ...prev,
                privacyPolicy: checked,
              }));
            }}
          />

          <PlaceOrderButtonWap
            shippingActuallyPrice={shippingActuallyPrice}
            grandTotal={grandTotal}
            currency={currency}
            goToPlaceOrder={goToPlaceOrder}
            paymentType={form.payment_type}
            loading={loading.placeOrder}
          />
        </section>
      )}

      {/* 弹窗 */}

      {repeatDialogShow && <RepeatPopup show={repeatDialogShow} />}

      <RetainPopup
        open={retainDialogShow}
        payPopData={payPopData}
        type={deepType}
        discountPrice={discountPrice}
        onReturnToCart={() => {
          setIsAllowShowRetain(false);
          setRetainDialogShow(false);
          setTimeout(() => {
            router.push('/cart?from=payment');
          }, 0);
        }}
        onApplyCode={applyPopCoupon}
        onClose={() => {
          handleCloseRetainPopup();
        }}
        onApplyRetainCoupon={() => {
          applyRetainCoupon(couponList[0]);
        }}
      />

      {showMemberShipValid.show && (
        <MemberShipGiftInvalid
          data={showMemberShipValid}
          onClose={() => {
            goCart();
          }}
          onContinue={() => {
            reloadPage();
          }}
        />
      )}
    </div>
  );
}
